import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AdminUser, type Role } from '../api';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Flash,
  PageHeader,
  Select,
  Spinner,
  Switch,
} from '../components/ui';
import { RoleBadge } from '../components/AppShell';
import { IconArrowRight, IconClose, IconSearch, IconUsers } from '../components/icons';

/**
 * Labels only. Which of these a given person may actually hand out comes from
 * the server, because the rule ("never above your own rank") has to hold there
 * whatever this screen decides to render.
 */
const ROLE_LABEL: Record<string, string> = {
  PENDING: 'No access',
  APPROVED_VIEWER: 'Approved requests viewer',
  REQUESTER: 'Requester',
  APPROVER: 'Approver',
  MTN_TECHNICIAN: 'MTN Technician',
  ADMIN: 'Administrator',
  SUPER_ADMIN: 'Super Admin',
  SYSTEM_ADMIN: 'System administrator',
};

/** One sentence per role, shown next to the picker so nobody guesses. */
const ROLE_DESCRIPTION: Record<string, string> = {
  PENDING: 'No access. They see only the waiting screen until a role is given.',
  APPROVED_VIEWER: 'Reads approved requests and their signed records. Cannot raise, approve or change anything.',
  REQUESTER: 'Raises enrolment and offboarding requests, signs and submits them, and follows their own.',
  APPROVER: 'Everything a requester can do, plus the shared approval queue: amend, return, reject, or approve and sign.',
  MTN_TECHNICIAN:
    'Everything a requester can do, plus the IT queue: implementation and completion sign-off. No users, catalogues or settings.',
  ADMIN: 'Full IT powers: the IT queue, users and roles, form settings, and every request.',
  SUPER_ADMIN: 'Everything an administrator can do, plus authority over administrators, and the only tier that can delete a record.',
  SYSTEM_ADMIN: 'The top tier, set in server configuration: system settings, the audit log and feedback.',
};

/** The order the list is sorted and the filter offers: most powerful first. */
const ROLE_ORDER: Role[] = [
  'SYSTEM_ADMIN',
  'SUPER_ADMIN',
  'ADMIN',
  'MTN_TECHNICIAN',
  'APPROVER',
  'REQUESTER',
  'APPROVED_VIEWER',
  'PENDING',
];

type Patch = Parameters<typeof api.updateUser>[1];
type StatusFilter = 'all' | 'active' | 'inactive';

function when(iso: string | null, fallback = 'never'): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : fallback;
}

export function AdminUsers() {
  const queryClient = useQueryClient();

  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: () => api.adminUsers() });
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.departments() });

  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Patch; name: string }) => api.updateUser(id, patch),
    onSuccess: (_result, { patch, name }) => {
      refresh();
      if (patch.role) setFlash(`${name} is now ${ROLE_LABEL[patch.role] === 'No access' ? 'without a role' : `a${/^[AEIO]/.test(ROLE_LABEL[patch.role] ?? '') ? 'n' : ''} ${ROLE_LABEL[patch.role]}`}.`);
      else if (patch.isActive === false) setFlash(`${name} can no longer sign in. The account and its history stay.`);
      else if (patch.isActive === true) setFlash(`${name} can sign in again.`);
      else if (patch.selfApprove !== undefined) setFlash(`${name} ${patch.selfApprove ? 'may now' : 'may no longer'} approve their own requests.`);
      else if (patch.departmentId !== undefined) setFlash(`Department for ${name} updated.`);
    },
  });

  const showAgain = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => api.resetUserWalkthrough(id),
    onSuccess: (_result, { name }) => {
      refresh();
      setFlash(`${name} will see the introduction again next time they sign in.`);
    },
  });

  // Deleting an account: a reason, the address typed back, and a clear
  // statement of what it does and does not do, mirroring deleting a request.
  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const [reason, setReason] = useState('');
  const [typedEmail, setTypedEmail] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (target: AdminUser) =>
      api.deleteUser(target.id, { reason: reason.trim(), confirmEmail: typedEmail.trim() }),
    onSuccess: ({ email }) => {
      setRemoving(null);
      setSelectedId(null);
      setReason('');
      setTypedEmail('');
      setFlash(`Removed ${email}. If they sign in again they arrive as a new account with no role.`);
      refresh();
    },
    onError: (err) => setRemoveError(err instanceof ApiError ? err.message : 'Could not remove the account.'),
  });

  const all = users.data?.users ?? [];
  const waiting = useMemo(() => all.filter((user) => user.role === 'PENDING'), [all]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all
      .filter((user) => user.role !== 'PENDING')
      .filter((user) => !roleFilter || user.role === roleFilter)
      .filter((user) => statusFilter === 'all' || (statusFilter === 'active' ? user.isActive : !user.isActive))
      .filter(
        (user) =>
          !needle ||
          user.displayName.toLowerCase().includes(needle) ||
          user.email.toLowerCase().includes(needle) ||
          (user.department?.name.toLowerCase().includes(needle) ?? false),
      )
      .sort(
        (a, b) =>
          ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.displayName.localeCompare(b.displayName),
      );
  }, [all, query, roleFilter, statusFilter]);

  const selected = all.find((user) => user.id === selectedId) ?? null;
  const busy = update.isPending || showAgain.isPending || remove.isPending;

  if (users.isPending) return <Spinner label="Loading users" />;
  if (users.isError) return <ErrorNote error={users.error} />;

  const { assignableRoles, canDeleteUsers } = users.data;
  const departmentOptions = departments.data?.departments ?? [];
  const rolesOffered = ROLE_ORDER.filter((role) => assignableRoles.includes(role));

  return (
    <div>
      <PageHeader
        title="Users & roles"
        description="Everyone who has signed in. Accounts start with no role and see nothing until you assign one. Open a person to change anything about their account."
        actions={
          waiting.length > 0 ? (
            <Badge tone="warning" dot>
              {waiting.length} waiting for access
            </Badge>
          ) : (
            <Badge tone="success" dot>
              Nobody waiting
            </Badge>
          )
        }
      />

      {update.isError && (
        <div className="mb-4">
          <ErrorNote error={update.error} />
        </div>
      )}
      {flash && (
        <div className="mb-4">
          <Flash message={flash} onDismiss={() => setFlash(null)} />
        </div>
      )}

      {waiting.length > 0 && (
        <section className="mb-8" aria-labelledby="waiting-heading">
          <h2
            id="waiting-heading"
            className="mb-3 text-xs font-semibold tracking-widest text-copper-700 uppercase dark:text-copper-400"
          >
            Waiting for access
          </h2>
          <Card className="divide-y divide-slate-100 border-copper-200 dark:divide-slate-800 dark:border-copper-900/60">
            {waiting.map((user) => (
              <div key={user.id} className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar name={user.displayName} />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900 dark:text-white">{user.displayName}</div>
                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {user.email} · first signed in {when(user.lastLoginAt, 'just now')}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    label={`Department for ${user.displayName}`}
                    value={user.department?.id ?? ''}
                    className="w-40"
                    disabled={busy}
                    onChange={(value) =>
                      update.mutate({ id: user.id, name: user.displayName, patch: { departmentId: value || null } })
                    }
                  >
                    <option value="">No department</option>
                    {departmentOptions.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label={`Give ${user.displayName} a role`}
                    value=""
                    className="w-52 border-copper-300 dark:border-copper-800"
                    disabled={busy}
                    onChange={(value) =>
                      value && update.mutate({ id: user.id, name: user.displayName, patch: { role: value as Role } })
                    }
                  >
                    <option value="">Give a role…</option>
                    {rolesOffered
                      .filter((role) => role !== 'PENDING')
                      .map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABEL[role]}
                        </option>
                      ))}
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedId(user.id)}>
                    More
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        </section>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* Full width on a phone, where three controls in one row crush the search to an icon. */}
        <label className="relative min-w-0 basis-full sm:flex-1 sm:basis-auto sm:max-w-xs">
          <IconSearch className="pointer-events-none absolute top-2.5 left-3 h-4 w-4 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name, email or department"
            aria-label="Search people"
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400"
          />
        </label>
        <Select label="Filter by role" value={roleFilter} className="w-48" onChange={(value) => setRoleFilter(value as Role | '')}>
          <option value="">Every role</option>
          {ROLE_ORDER.filter((role) => role !== 'PENDING').map((role) => (
            <option key={role} value={role}>
              {ROLE_LABEL[role]}
            </option>
          ))}
        </Select>
        <Select label="Filter by status" value={statusFilter} className="w-36" onChange={(value) => setStatusFilter(value as StatusFilter)}>
          <option value="all">Active and not</option>
          <option value="active">Active only</option>
          <option value="inactive">Deactivated</option>
        </Select>
        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {filtered.length} of {all.length - waiting.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<IconUsers />}
          title={all.length - waiting.length === 0 ? 'Nobody has a role yet' : 'Nobody matches'}
          body={
            all.length - waiting.length === 0
              ? 'As soon as you give somebody a role above, they appear here.'
              : 'Try a shorter search, or clear the role and status filters.'
          }
        />
      ) : (
        <Card className="divide-y divide-slate-100 dark:divide-slate-800">
          {filtered.map((user) => (
            <PersonRow key={user.id} user={user} selected={user.id === selectedId} onOpen={() => setSelectedId(user.id)} />
          ))}
        </Card>
      )}

      <details className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        <summary className="cursor-pointer font-medium text-slate-700 select-none dark:text-slate-300">
          How roles and permissions work
        </summary>
        <p className="mt-2">
          <strong className="font-medium text-slate-700 dark:text-slate-300">Self-approve</strong> lets someone
          approve a request they raised themselves. It is off by default, and every use of it is recorded on the
          request and in the audit log. You cannot grant it to yourself, change your own role, deactivate
          yourself, or remove the last administrator. You also cannot hand out a role above your own, since
          promoting a colleague and having them promote you back would be a way round all of it.
        </p>
        <p className="mt-2">
          <strong className="font-medium text-slate-700 dark:text-slate-300">System administrators</strong> come
          from <code>SYSTEM_ADMIN_UPNS</code> in the server configuration and cannot be edited here by anyone,
          including themselves. That is deliberate: a role that could be granted from inside the application
          would be no protection at all.
        </p>
        <p className="mt-2">
          <strong className="font-medium text-slate-700 dark:text-slate-300">MTN Technician</strong> grants
          access to the IT queue and nothing else. Only a system administrator can move someone into or out of
          it, in either direction, since it sits outside the ordinary rank ladder.
        </p>
        {canDeleteUsers && (
          <p className="mt-2">
            <strong className="font-medium text-slate-700 dark:text-slate-300">Deleting an account</strong> is
            for a wrong sign-in or a test account: one that has never raised, approved, implemented, signed or
            discussed a request. Anything with history is refused, because a signed record must keep every name
            on it; deactivate those instead. Every deletion is recorded in the audit log with its reason.
          </p>
        )}
      </details>

      {selected && (
        <UserPanel
          user={selected}
          departments={departmentOptions}
          assignableRoles={assignableRoles}
          canDelete={canDeleteUsers}
          busy={busy}
          onClose={() => setSelectedId(null)}
          onChange={(patch) => update.mutate({ id: selected.id, name: selected.displayName, patch })}
          onShowAgain={() => showAgain.mutate({ id: selected.id, name: selected.displayName })}
          onDelete={() => {
            setRemoveError(null);
            setReason('');
            setTypedEmail('');
            setRemoving(selected);
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.displayName}'s account?`}
          confirmLabel="Delete account"
          busy={remove.isPending}
          confirmDisabled={
            reason.trim().length < 10 || typedEmail.trim().toLowerCase() !== removing.email.toLowerCase()
          }
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            setRemoveError(null);
            remove.mutate(removing);
          }}
          body={
            <>
              <p>
                This removes the account for {removing.email}: its role, department, grants and notifications.
                It has never been part of a request, so no record loses a name. Their sign-ins stay in the audit
                log, without the account attached; the log keeps who they were.
              </p>
              <p className="mt-2">
                It does not touch Microsoft Entra. If the same person signs in again they arrive as a new
                account with no role, and you are told as usual. To keep somebody out without removing them,{' '}
                <strong>deactivate</strong> instead.
              </p>

              <label className="mt-4 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Why is it being removed?</span>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={2}
                  placeholder="Signed in with the wrong account"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </label>
              <label className="mt-3 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Type <code>{removing.email}</code> to confirm
                </span>
                <input
                  value={typedEmail}
                  onChange={(event) => setTypedEmail(event.target.value)}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </label>
              {removeError && (
                <div className="mt-3">
                  <Alert>{removeError}</Alert>
                </div>
              )}
            </>
          }
        />
      )}
    </div>
  );
}

/** One line per person, enough to find them; everything else is in the panel. */
function PersonRow({ user, selected, onOpen }: { user: AdminUser; selected: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-current={selected ? 'true' : undefined}
      className={`press group flex w-full flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5 text-left transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40 ${
        selected ? 'bg-brand-50/60 dark:bg-brand-950/30' : ''
      }`}
    >
      <div className="order-1 flex min-w-0 flex-1 basis-56 items-center gap-3">
        <Avatar name={user.displayName} />
        <div className="min-w-0">
          <div className={`truncate font-medium ${user.isActive ? 'text-slate-900 dark:text-white' : 'text-slate-500 line-through dark:text-slate-400'}`}>
            {user.displayName}
          </div>
          <div className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
        </div>
      </div>

      {/* On a phone the badges wrap to their own line under the name, and the
          arrow stays on the first line at the right edge. */}
      <div className="order-3 flex basis-full flex-wrap items-center gap-1.5 sm:order-none sm:w-56 sm:basis-auto">
        <RoleBadge role={user.role} />
        {user.selfApprove && <Badge tone="copper">Self-approve</Badge>}
        {!user.isActive && <Badge tone="neutral">Deactivated</Badge>}
      </div>

      <div className="hidden w-40 truncate text-sm text-slate-600 md:block dark:text-slate-400">
        {user.department?.name ?? <span className="text-slate-400 dark:text-slate-500">No department</span>}
      </div>

      <div className="hidden w-28 text-xs whitespace-nowrap text-slate-500 lg:block dark:text-slate-400">
        {user.lastLoginAt ? `Seen ${when(user.lastLoginAt)}` : 'Never signed in'}
      </div>

      <IconArrowRight className="order-2 h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 sm:order-last dark:text-slate-500" />
    </button>
  );
}

/**
 * Everything about one person, with each control labelled and explained,
 * in a panel that slides in from the right. The list stays a list.
 */
function UserPanel({
  user,
  departments,
  assignableRoles,
  canDelete,
  busy,
  onClose,
  onChange,
  onShowAgain,
  onDelete,
}: {
  user: AdminUser;
  departments: { id: string; name: string }[];
  assignableRoles: Role[];
  canDelete: boolean;
  busy: boolean;
  onClose: () => void;
  onChange: (patch: Patch) => void;
  onShowAgain: () => void;
  onDelete: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The server refuses these changes too; this only stops the panel offering
  // something that will bounce.
  const locked = !user.manageable;
  // MTN Technician sits outside the rank ladder: only a system administrator
  // may move someone into or out of it. A viewer's own assignableRoles list
  // already tells us whether they are one, since only a system administrator's
  // list includes it at all.
  const roleChangeLocked = user.role === 'MTN_TECHNICIAN' && !assignableRoles.includes('MTN_TECHNICIAN');
  const rolesOffered = [...new Set([user.role, ...ROLE_ORDER.filter((role) => assignableRoles.includes(role))])];

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={`${user.displayName}'s account`}>
      <div className="animate-fade absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <aside className="animate-rise relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start gap-3 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <Avatar name={user.displayName} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-white">{user.displayName}</h2>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <RoleBadge role={user.role} />
              {!user.isActive && <Badge tone="neutral">Deactivated</Badge>}
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mt-1 -mr-2 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <IconClose className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-7 px-6 py-6">
          {locked && (
            <Alert tone="info">
              {user.role === 'SYSTEM_ADMIN'
                ? 'A system administrator, set in the server configuration. Nobody can change this account here.'
                : `${ROLE_LABEL[user.role] ?? user.role}, at or above your own level. You can see this account but not change it.`}
            </Alert>
          )}

          <Section title="Access">
            <Field label="Role" hint={ROLE_DESCRIPTION[user.role]}>
              <Select
                label={`Role for ${user.displayName}`}
                value={user.role}
                className="w-full"
                disabled={busy || locked || roleChangeLocked}
                onChange={(value) => onChange({ role: value as Role })}
              >
                {rolesOffered.map((role) => (
                  <option key={role} value={role} disabled={!assignableRoles.includes(role)}>
                    {ROLE_LABEL[role] ?? role}
                  </option>
                ))}
              </Select>
              {roleChangeLocked && (
                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                  Only a system administrator can move someone into or out of MTN Technician.
                </p>
              )}
            </Field>
            <Field label="Department" hint="Recorded on the account and on each request they raise. It does not route anything.">
              <Select
                label={`Department for ${user.displayName}`}
                value={user.department?.id ?? ''}
                className="w-full"
                disabled={busy}
                onChange={(value) => onChange({ departmentId: value || null })}
              >
                <option value="">No department</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </Select>
            </Field>
          </Section>

          <Section title="Permissions">
            <ToggleRow
              id={`self-approve-${user.id}`}
              checked={user.selfApprove}
              disabled={busy || locked}
              onChange={(next) => onChange({ selfApprove: next })}
              label="Can approve their own requests"
              hint="Off by default. Every use is recorded on the request and in the audit log."
            />
            <ToggleRow
              id={`active-${user.id}`}
              checked={user.isActive}
              disabled={busy || locked}
              onChange={(next) => onChange({ isActive: next })}
              label="Account active"
              hint={
                user.isActive
                  ? 'Turn off to stop them signing in. The account and everything it did stay on record.'
                  : 'Deactivated: they cannot sign in. Turn on to let them back in.'
              }
            />
          </Section>

          <Section title="Introduction">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {user.walkthroughSeenAt
                  ? `Seen on ${when(user.walkthroughSeenAt)}.`
                  : 'Not seen yet: it shows the next time they sign in.'}
              </p>
              {!locked && user.walkthroughSeenAt && (
                <Button size="sm" variant="secondary" disabled={busy} onClick={onShowAgain}>
                  Show it again
                </Button>
              )}
            </div>
          </Section>

          <Section title="Activity">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {user.lastLoginAt ? `Last signed in ${when(user.lastLoginAt)}.` : 'Has never signed in.'}
              {user.historyNote ? ` ${user.historyNote}` : ' Has not raised, approved or worked any request yet.'}
            </p>
          </Section>

          {canDelete && !locked && (
            <Section title="Remove">
              {user.deletable ? (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    This account has no history, so it can be removed outright: for a wrong sign-in or a test
                    account. Everything else is better deactivated.
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={onDelete}
                    className="mt-3 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                  >
                    Delete account
                  </Button>
                </>
              ) : (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  This account is named on records, so it cannot be deleted. Deactivate it instead; that keeps
                  every record it is part of intact.
                </p>
              )}
            </Section>
          )}
        </div>
      </aside>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      {children}
      {hint && <p className="mt-1.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  );
}

function ToggleRow({
  id,
  checked,
  disabled,
  onChange,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Switch id={id} checked={checked} disabled={disabled} onChange={onChange} label={label} />
      <div className="min-w-0">
        <label htmlFor={id} className="block cursor-pointer text-sm font-medium text-slate-800 dark:text-slate-200">
          {label}
        </label>
        <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">{hint}</p>
      </div>
    </div>
  );
}

function ErrorNote({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Something went wrong.';
  return <Alert>{message}</Alert>;
}
